import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";

type WeightUnit = "lbs" | "kg";

interface WeightUnitContextType {
  unit: WeightUnit;
  setUnit: (unit: WeightUnit) => void;
  toggleUnit: () => void;
  formatWeight: (value: number, options?: { showUnit?: boolean; decimals?: number }) => string;
  convertToDisplay: (lbs: number) => number;
  convertToStorage: (displayValue: number) => number;
  getUnitLabel: (perHand?: boolean) => string;
}

const WeightUnitContext = createContext<WeightUnitContextType | undefined>(undefined);

const STORAGE_KEY = "weight-unit-preference";
const LBS_TO_KG = 0.453592;
const KG_TO_LBS = 2.20462;

export function WeightUnitProvider({ children }: { children: ReactNode }) {
  const [unit, setUnitState] = useState<WeightUnit>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "kg" || stored === "lbs") {
        return stored;
      }
    }
    return "lbs";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, unit);
  }, [unit]);

  const setUnit = useCallback((newUnit: WeightUnit) => {
    setUnitState(newUnit);
  }, []);

  const toggleUnit = useCallback(() => {
    setUnitState((prev) => (prev === "lbs" ? "kg" : "lbs"));
  }, []);

  // Convert from storage (lbs) to display unit
  const convertToDisplay = useCallback(
    (lbs: number): number => {
      if (unit === "kg") {
        return lbs * LBS_TO_KG;
      }
      return lbs;
    },
    [unit]
  );

  // Convert from display unit back to storage (lbs)
  const convertToStorage = useCallback(
    (displayValue: number): number => {
      if (unit === "kg") {
        return displayValue * KG_TO_LBS;
      }
      return displayValue;
    },
    [unit]
  );

  // Format weight value with optional unit suffix
  const formatWeight = useCallback(
    (value: number, options: { showUnit?: boolean; decimals?: number } = {}): string => {
      const { showUnit = true, decimals = 1 } = options;
      const displayValue = convertToDisplay(value);
      
      // Format large numbers
      let formatted: string;
      if (displayValue >= 1000000) {
        formatted = `${(displayValue / 1000000).toFixed(1)}M`;
      } else if (displayValue >= 10000) {
        formatted = `${(displayValue / 1000).toFixed(1)}K`;
      } else if (displayValue >= 1000) {
        formatted = `${(displayValue / 1000).toFixed(decimals)}K`;
      } else if (Number.isInteger(displayValue)) {
        formatted = displayValue.toString();
      } else {
        formatted = displayValue.toFixed(decimals);
      }

      if (showUnit) {
        return `${formatted} ${unit}`;
      }
      return formatted;
    },
    [unit, convertToDisplay]
  );

  // Get unit label with optional per-hand suffix for dumbbell exercises
  const getUnitLabel = useCallback(
    (perHand?: boolean): string => {
      if (perHand) {
        return unit === "kg" ? "kg/hand" : "lbs/hand";
      }
      return unit;
    },
    [unit]
  );

  return (
    <WeightUnitContext.Provider
      value={{
        unit,
        setUnit,
        toggleUnit,
        formatWeight,
        convertToDisplay,
        convertToStorage,
        getUnitLabel,
      }}
    >
      {children}
    </WeightUnitContext.Provider>
  );
}

export function useWeightUnit() {
  const context = useContext(WeightUnitContext);
  if (context === undefined) {
    throw new Error("useWeightUnit must be used within a WeightUnitProvider");
  }
  return context;
}
